/**
 * Page `/confirmer/:id` — atterrissage du bouton "Je confirme ma présence"
 * du mail de confirmation. Appelle `POST /api/preregistrations/:id/attend`
 * dès le montage (idempotent) puis affiche le résultat.
 *
 * Trois états :
 *   * `loading`  — la requête est en cours
 *   * `success`  — la résa est marquée "présence confirmée"
 *   * `error`    — UUID inconnu, résa annulée, problème réseau...
 *
 * Le user n'a rien à faire : ce design "1 clic = c'est fait" maximise
 * le taux de confirmation (pas de formulaire de re-validation).
 */
import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { confirmAttendance, extractApiError } from '../api.js';

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function ConfirmAttendance() {
  const { id } = useParams();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    confirmAttendance(id)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setStatus('success');
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        setError(
          code === 404
            ? "Cette préréservation est introuvable. Vérifiez votre lien ou contactez le club."
            : extractApiError(err, 'Impossible de confirmer votre présence pour le moment.'),
        );
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <Container maxWidth="sm">
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: 1, borderColor: 'divider' }}>
        {status === 'loading' && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography color="text.secondary">Confirmation en cours…</Typography>
          </Stack>
        )}

        {status === 'success' && data && (
          <Stack spacing={3}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main' }} />
              <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
                Merci {data.first_name} !
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Votre présence est <strong>confirmée</strong> pour la soirée.
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: 'success.main',
                color: 'success.contrastText',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <EventOutlinedIcon />
              <Typography variant="body2">
                {data.event_name} · {formatEventDate(data.event_date)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Récapitulatif :
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                <strong>{data.adults}</strong> adulte{data.adults > 1 ? 's' : ''} ·{' '}
                <strong>{data.children}</strong> enfant{data.children > 1 ? 's' : ''} ·{' '}
                <strong>{data.meals}</strong> repas
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                component={RouterLink}
                to={`/payer/${data.id}`}
                variant="contained"
                fullWidth
                startIcon={<ConfirmationNumberOutlinedIcon />}
              >
                Voir mon récapitulatif
              </Button>
              <Button
                component={RouterLink}
                to="/infos"
                variant="outlined"
                fullWidth
              >
                Infos pratiques & plan
              </Button>
            </Stack>
          </Stack>
        )}

        {status === 'error' && (
          <Stack spacing={2}>
            <Alert severity="warning">{error}</Alert>
            <Button
              component={RouterLink}
              to="/"
              variant="outlined"
              startIcon={<HomeOutlinedIcon />}
            >
              Retour à l'accueil
            </Button>
          </Stack>
        )}
      </Paper>
    </Container>
  );
}
